import { Alert, Box, Button, Code, Drawer, Group, Loader, Stack, Tabs, Text } from '@mantine/core';
import { useRef, useState } from 'react';
import { EstatePill, KV, KVRow, KVSection, Pill, uiClasses } from '../../ui';
import { dateTime, latency, tokens as fmtTokens } from '../../ui/format';
import { tokens as T, statusTone } from '../../theme/tokens';
import { useUIStore } from '../../stores/uiStore';
import { useLedgerStore } from '../../stores/dataStores';
import { effectiveBillingMode, entryId } from '../../data/types';
import { getSources } from '../../stores/sources';
import { reads, reveal, type CapturedPayload, type PayloadRead } from '../../api/capture';

/** What to say about the classifier when the row carries no router entry.
 *  The decision trail is the only witness: a call the classifier was never
 *  needed for reads differently from one it timed out on, and "not run" is a
 *  lie for the latter — the attempt happened, it just recorded no usage. */
export function classifierOutcome(reason: string | undefined): string {
  const trail = reason ?? '';
  if (trail.includes('classifier_failed:')) return 'attempted — failed, no recorded usage';
  if (trail.includes('classifier_open')) return 'attempted — circuit open, fell back';
  if (trail.includes('classifier_skipped:')) return `skipped — ${trail.split('classifier_skipped:')[1]?.split(';')[0] ?? ''}`;
  if (trail.includes('classifier:off')) return 'off for this estate';
  return 'not needed — the rules decided';
}
import { ApiError } from '../../api/client';

/* One usage row, in full.
 *
 * A Drawer rather than a hand-rolled fixed panel, for the focus trap, the
 * scroll lock and Escape — all three of which the design's own markup
 * re-implements and two of which it gets wrong. The header is ours: it is a
 * two-row composite with three badges and an action, which is not the shape
 * Drawer.Title has. */
/** The stored prompt and response for one usage row.
 *
 *  Nothing is fetched until the operator asks. That is not laziness: revealing
 *  a payload writes an audit row naming them, so a tab that loaded on render
 *  would fill the trail with reads nobody performed and make the real ones
 *  harder to find. */
function PayloadTab({ requestId }: { requestId: string }) {
  // Keyed by request id at the call site, so switching rows REMOUNTS this and
  // no state survives. The guard below is the second half: a reveal already in
  // flight resolves after the remount, and its content must not land under a
  // different row's metadata — the read was audited for the row that asked.
  const mine = useRef(requestId);
  mine.current = requestId;
  const [state, setState] = useState<'idle' | 'loading' | 'shown' | 'missing' | 'error'>('idle');
  const [payload, setPayload] = useState<CapturedPayload | null>(null);
  const [trail, setTrail] = useState<PayloadRead[]>([]);
  const [message, setMessage] = useState('');

  async function show() {
    setState('loading');
    try {
      const p = await reveal(requestId);
      if (mine.current !== requestId) return;
      setPayload(p);
      setState('shown');
      const trailRows = await reads(requestId);
      if (mine.current !== requestId) return;
      setTrail(trailRows);
    } catch (err) {
      if (mine.current !== requestId) return;
      const msg = err instanceof Error ? err.message : String(err);
      // A missing payload is the NORMAL answer — capture is off for most
      // estates and payloads expire — not a fault to report as one.
      setState(err instanceof ApiError && err.status === 404 ? 'missing' : 'error');
      setMessage(msg);
    }
  }

  if (state === 'idle') {
    return (
      <Stack gap={14}>
        <Alert color="yellow" variant="light" p="12px 16px">
          <Text fz={13} lh={1.6}>
            Revealing this writes an audit row naming you, the request id and the tenant. The
            estate can see it. That is what makes an operator reading a customer's prompt
            defensible, so it is not optional and not silent.
          </Text>
        </Alert>
        <Group>
          <Button loading={false} onClick={() => void show()}>
            Reveal prompt &amp; response
          </Button>
        </Group>
      </Stack>
    );
  }
  if (state === 'loading') {
    return (
      <Group gap={12}>
        <Loader size="sm" />
        <Text fz={13} c={T.ink3}>
          Recording the read, then fetching…
        </Text>
      </Group>
    );
  }
  if (state === 'missing') {
    return (
      <Box style={{ border: `1px dashed ${T.lineHard}`, borderRadius: 12, padding: 22 }}>
        <Text fz={13} c={T.ink4} lh={1.65}>
          No stored payload for this request. Capture is off for most estates, and a captured
          payload expires on the estate's retention — so this is the ordinary answer, not a fault.
        </Text>
      </Box>
    );
  }
  if (state === 'error' || !payload) {
    return (
      <Alert color="red" variant="light">
        <Text fz={13}>{message}</Text>
      </Alert>
    );
  }

  return (
    <Stack gap={16}>
      <Group gap={10} wrap="wrap">
        <Pill tone="neutral" size="md">CAPTURE {payload.mode.toUpperCase()}</Pill>
        <Text fz={12} c={T.ink4}>expires {dateTime(payload.expireAt)}</Text>
        {payload.truncated ? (
          <Pill tone="warn" size="md">TRUNCATED</Pill>
        ) : null}
      </Group>
      <Box>
        <Text fz={10} fw={700} c={T.ink4} style={{ letterSpacing: '0.09em' }} mb={6}>
          PROMPT
        </Text>
        <Code block style={{ maxHeight: 260, overflow: 'auto' }}>{payload.request}</Code>
      </Box>
      <Box>
        <Text fz={10} fw={700} c={T.ink4} style={{ letterSpacing: '0.09em' }} mb={6}>
          RESPONSE
        </Text>
        <Code block style={{ maxHeight: 260, overflow: 'auto' }}>{payload.response}</Code>
      </Box>
      <Box>
        <Text fz={10} fw={700} c={T.ink4} style={{ letterSpacing: '0.09em' }} mb={6}>
          WHO HAS READ THIS
        </Text>
        <Stack gap={6}>
          {trail.map((t) => (
            <Group key={t.id} gap={10}>
              <Text fz={12.5} fw={600}>{t.operator}</Text>
              <Text fz={12} c={T.ink4}>{dateTime(t.readAt)}</Text>
            </Group>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}

export function LedgerDrawer() {
  const selection = useUIStore((s) => s.ledgerSelection);
  const select = useUIStore((s) => s.selectLedgerRow);
  const tab = useUIStore((s) => s.drawerTab);
  const setUI = useUIStore((s) => s.set);
  // Subscribe to the collection and look the row up outside the selector: a
  // selector that closes over `selection` is re-created on every render and is
  // easy to reason about wrongly, and the collection reference is stable
  // between deltas anyway.
  const ledger = useLedgerStore((s) => s.data);
  const row = selection ? ledger.byId[selection] : undefined;
  const canMutate = getSources().canMutate;

  const provider = row?.provider ?? '';
  const mode = row ? effectiveBillingMode(row) : 'billable';

  return (
    <Drawer
      opened={Boolean(row)}
      onClose={() => select(null)}
      position="right"
      size={760}
      padding={0}
      withCloseButton={false}
      overlayProps={{ backgroundOpacity: 0.35, color: T.ink }}
      styles={{
        content: {
          background: T.card,
          borderLeft: `1px solid ${T.lineHard}`,
          // Only the left pair — Drawer's own radius prop rounds all four.
          borderRadius: '16px 0 0 16px',
          boxShadow: '-20px 0 50px rgba(46,42,38,0.26)',
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          flex: '1 1 auto',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: 0,
        },
      }}
    >
      {row ? (
        <>
          <Group
            justify="space-between"
            align="flex-start"
            gap={14}
            px={22}
            py={18}
            style={{ background: T.rail, flex: '0 0 auto' }}
          >
            <Box style={{ minWidth: 0 }}>
              <Text fz={10} fw={700} c={T.ink4} style={{ letterSpacing: '0.09em' }}>
                USAGE ROW
              </Text>
              <Text ff="monospace" fz={15} fw={600} mt={5} style={{ wordBreak: 'break-all' }}>
                {row.eventId}
              </Text>
              <Group gap={8} mt={9} wrap="wrap">
                <EstatePill estate={row.estateId} size="md" />
                <Pill tone={row.conflictOf ? 'fail' : statusTone(row.status)} size="md">
                  {row.conflictOf ? 'CONFLICT' : row.status.toUpperCase()}
                </Pill>
                <Pill tone="neutral" size="md">
                  CAPTURE OFF
                </Pill>
                <Text fz={12} c={T.ink3}>
                  {dateTime(row.occurredAt)}
                </Text>
              </Group>
            </Box>
            <Group gap={12} wrap="nowrap" style={{ flex: '0 0 auto' }}>
              <Button variant="default" size="xs" disabled={!canMutate}>
                Re-emit
              </Button>
              <Text
                fz={18}
                c={T.ink3}
                style={{ cursor: 'pointer' }}
                onClick={() => select(null)}
                role="button"
                aria-label="Close"
              >
                ✕
              </Text>
            </Group>
          </Group>

          <Tabs
            value={tab}
            onChange={(v) => setUI('drawerTab', (v ?? 'overview') as typeof tab)}
            classNames={{ root: uiClasses.tabs }}
            styles={{
              list: { paddingInline: 22, background: T.rail },
              root: { '--tab-border-color': T.lineHard },
            }}
          >
            <Tabs.List>
              <Tabs.Tab value="overview">Overview</Tabs.Tab>
              <Tabs.Tab value="payload">Prompt &amp; response</Tabs.Tab>
              <Tabs.Tab value="audit">Capture &amp; access</Tabs.Tab>
            </Tabs.List>
          </Tabs>

          <Box style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', padding: '18px 22px' }}>
            {tab === 'overview' ? (
              <Box
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))',
                  gap: 16,
                  alignContent: 'start',
                }}
              >
                <KVSection title="Dedup identity">
                  <KVRow>
                    <KV k="Estate" v={row.estateId} mono />
                  </KVRow>
                  <KVRow>
                    <KV k="Tenant" v={row.tenantId} mono />
                  </KVRow>
                  <KVRow>
                    <KV k="Event id" v={row.eventId} mono />
                  </KVRow>
                  <KVRow>
                    <KV k="Lane" v={row.lane || 'none'} mono />
                  </KVRow>
                  <KVRow>
                    <KV k="Request id" v={row.requestId} mono fg={T.ink4} />
                  </KVRow>
                  <KVRow>
                    <KV
                      k="Conflict of"
                      v={row.conflictOf ?? '—'}
                      fg={row.conflictOf ? T.coralDeep : T.ink4}
                    />
                  </KVRow>
                </KVSection>

                <KVSection title="Caller">
                  <KVRow>
                    <KV k="Deployment" v={row.deploymentId} mono />
                  </KVRow>
                  <KVRow>
                    <KV k="Auth lane" v={row.authLane || 'token'} />
                  </KVRow>
                  <KVRow>
                    <KV k="Subject" v={row.subject} mono fg={T.ink4} />
                  </KVRow>
                  <KVRow>
                    <KV
                      k="Billing mode"
                      v={mode}
                      fg={mode === 'billable' ? T.coralDeep : T.ink3}
                    />
                  </KVRow>
                </KVSection>

                <KVSection title="Completion">
                  <KVRow>
                    <KV k="Provider" v={provider} />
                  </KVRow>
                  <KVRow>
                    <KV k="Request model" v={row.requestModel} mono />
                  </KVRow>
                  <KVRow>
                    <KV k="Served model" v={row.servedModel} mono fg={T.ink4} />
                  </KVRow>
                  <KVRow>
                    <KV k="Credential" v={row.poolKeyId ?? '—'} mono fg={T.ink4} />
                  </KVRow>
                  <KVRow>
                    <KV k="Status" v={row.status} />
                  </KVRow>
                  <KVRow>
                    <KV k="Latency" v={latency(row.latencyMs)} />
                  </KVRow>
                </KVSection>

                {row.routedFrom ? (
                  <KVSection title="Routing">
                    <KVRow>
                      <KV k="Asked for" v={row.routedFrom} mono />
                    </KVRow>
                    <KVRow>
                      <KV k="Tier" v={row.routeTier ?? '—'} />
                    </KVRow>
                    <KVRow>
                      <KV k="Decision" v={row.routeReason ?? '—'} mono fg={T.ink4} />
                    </KVRow>
                    <KVRow>
                      <KV
                        k="Classifier"
                        v={
                          row.router
                            ? `${row.router.provider}/${row.router.requestModel} — ${fmtTokens(row.router.usage.inputTokens)} in, ${latency(row.router.latencyMs)}, paired entry under ${entryId(row.eventId, 'router')}`
                            : classifierOutcome(row.routeReason)
                        }
                        fg={row.router ? undefined : T.ink4}
                      />
                    </KVRow>
                    {row.router?.forecast ? (
                      <KVRow>
                        <KV
                          k="Forecast"
                          v={`${row.router.forecast.card}: p=${row.router.forecast.pSolve.toFixed(2)} ${row.router.forecast.boundary}${row.router.forecast.task ? ` · ${row.router.forecast.task}` : ''}`}
                          mono
                          fg={T.ink4}
                        />
                      </KVRow>
                    ) : null}
                  </KVSection>
                ) : null}

                {row.search ? (
                  <KVSection title="Search unit">
                    <KVRow>
                      <KV k="Backend" v={row.search.provider} mono />
                    </KVRow>
                    <KVRow>
                      <KV k="Queries issued" v={String(row.search.queries)} />
                    </KVRow>
                    <KVRow>
                      <KV k="Results" v={String(row.search.results)} />
                    </KVRow>
                  </KVSection>
                ) : null}

                {row.memory ? (
                  <KVSection title="Memory unit">
                    <KVRow>
                      <KV k="Operation" v={row.memory.op} mono />
                    </KVRow>
                    <KVRow>
                      <KV k="Items" v={String(row.memory.items)} />
                    </KVRow>
                    <KVRow>
                      <KV k="Hits" v={String(row.memory.hits)} />
                    </KVRow>
                    <KVRow>
                      <KV k="Billed" v="never — ground truth only (DR-73)" fg={T.ink4} />
                    </KVRow>
                  </KVSection>
                ) : null}

                {row.compact ? (
                  <KVSection title="Compaction">
                    <KVRow>
                      <KV k="Folds" v={String(row.compact.folds)} />
                    </KVRow>
                    <KVRow>
                      <KV k="History" v={`~${row.compact.tokensBefore.toLocaleString()} → ~${row.compact.tokensAfter.toLocaleString()} tokens`} />
                    </KVRow>
                    <KVRow>
                      <KV k="Summariser" v={row.servedModel || '—'} mono />
                    </KVRow>
                    <KVRow>
                      <KV
                        k="Billed"
                        v={row.compact.folds === 0 ? 'never — a foldless pass spent nothing (DR-77)' : "as the completion it is; the folds are the estate's unit (DR-76)"}
                        fg={T.ink4}
                      />
                    </KVRow>
                  </KVSection>
                ) : null}

                <KVSection title="Tokens">
                  <KVRow>
                    <KV k="Input" v={fmtTokens(row.primary.inputTokens)} />
                  </KVRow>
                  <KVRow>
                    <KV k="Output" v={fmtTokens(row.primary.outputTokens)} />
                  </KVRow>
                  <KVRow>
                    <KV k="Cache write" v={fmtTokens(row.primary.cacheCreationTokens)} />
                  </KVRow>
                  <KVRow>
                    <KV k="Cache read" v={fmtTokens(row.primary.cachedTokens)} />
                  </KVRow>
                  <KVRow>
                    <KV
                      k="Refused attempt"
                      v={row.refused ? `yes — paired entry under ${row.eventId}:refused` : 'none'}
                      fg={row.refused ? T.marigoldFg : T.ink4}
                    />
                  </KVRow>
                </KVSection>

                <KVSection title="Emission">
                  <KVRow>
                    <KV k="Occurred at" v={dateTime(row.occurredAt)} mono />
                  </KVRow>
                  <KVRow>
                    <KV k="Ledger expiry" v={dateTime(row.expireAt)} />
                  </KVRow>
                  <KVRow>
                    <KV k="Delivery" v="found by reconciliation" fg={T.ink4} />
                  </KVRow>
                </KVSection>
              </Box>
            ) : null}

            {tab === 'payload' ? <PayloadTab key={row.requestId} requestId={row.requestId} /> : null}

            {tab === 'audit' ? (
              <Stack gap={18}>
                <KVSection title="Capture policy in force">
                  <KVRow>
                    <KV k="Mode" v="off — not implemented" fg={T.ink4} />
                  </KVRow>
                  <KVRow>
                    <KV k="Stored payloads" v="none" fg={T.ink4} />
                  </KVRow>
                </KVSection>
                <Text fz={12} c={T.ink4} lh={1.6}>
                  When capture exists, every read of a stored payload writes an audit row naming the
                  operator and the event id, visible to the estate and to the tenant. That is the
                  property that makes an operator-readable prompt defensible at all, so it lands with
                  the feature rather than after it.
                </Text>
              </Stack>
            ) : null}
          </Box>
        </>
      ) : null}
    </Drawer>
  );
}
